<template>
  <div v-loading="loading">
    <div class="fc-hero">
      <div>
        <div class="fc-hero__badge">Davlat sektori qarzi statistikasi</div>
        <h2 class="fc-hero__title">Davriyliklar</h2>
        <p class="fc-hero__sub">
          Kalendar kataklaridagi rangli badge turlari: Oylik, Choraklik, Yillik, Kunlik.
          Rang va nom portaldagi legend'da shu yerdan oladi.
        </p>
      </div>
      <div class="fc-hero__actions">
        <el-button plain @click="fetchList">Yangilash</el-button>
        <el-button type="primary" @click="openCreate">+ Davriylik qo'shish</el-button>
      </div>
    </div>

    <MfEmpty
        v-if="!loading && !rows.length"
        title="Davriyliklar topilmadi"
        description="Yangi davriylik yaratish uchun yuqoridagi tugmani bosing"
    />

    <div v-else class="fc-table-wrap">
      <el-table :data="rows" :row-class-name="rowClass">
        <el-table-column label="Nomi" min-width="240">
          <template #default="scope">
            <div class="fc-cell-title">
              <span class="fc-cell-title__main">{{ title(scope.row) }}</span>
              <span class="fc-langs">
                <span
                    v-for="lt in langTabs"
                    :key="lt.code"
                    class="fc-lang-chip"
                    :class="{'is-filled': !!scope.row.title?.[lt.code]}"
                    :title="langLabels[lt.code]"
                >{{ lt.tabLabel }}</span>
              </span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Kod" width="150">
          <template #default="scope">
            <code class="fc-code">{{ scope.row.code }}</code>
          </template>
        </el-table-column>

        <el-table-column label="Rang" width="140">
          <template #default="scope">
            <span class="fc-color-chip">
              <span class="fc-color-chip__dot" :style="{background: scope.row.color}"/>
              {{ scope.row.color }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Namuna" width="110" align="center">
          <template #default="scope">
            <span class="fc-badge" :style="badgeStyle(scope.row)">15</span>
          </template>
        </el-table-column>

        <el-table-column label="Tartib" width="90" align="center" prop="order_number"/>

        <el-table-column label="Holati" width="110" align="center">
          <template #default="scope">
            <el-switch
                :model-value="scope.row.is_active !== false"
                :loading="togglingId === scope.row.id"
                style="--el-switch-on-color: #16A34A"
                @change="onToggleActive(scope.row)"
            />
          </template>
        </el-table-column>

        <el-table-column fixed="right" label="Amallar" width="120" align="center">
          <template #default="scope">
            <el-tooltip content="Tahrirlash" placement="top" :hide-after="0">
              <el-button size="small" type="primary" :icon="Edit" @click="openEdit(scope.row)"/>
            </el-tooltip>
            <el-tooltip content="O'chirish" placement="top" :hide-after="0">
              <el-button
                  size="small"
                  type="danger"
                  :icon="Delete"
                  :loading="deletingId === scope.row.id"
                  @click="onDelete(scope.row)"
              />
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <PeriodicityDialog
        v-model="dialogVisible"
        :periodicity="editing"
        :count="rows.length"
        :next-order="nextOrder"
        @saved="fetchList"
    />
  </div>
</template>

<script>
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import { Delete, Edit } from "@element-plus/icons-vue";
import MfEmpty from "@/components/core/MfEmpty.vue";
import PeriodicityDialog from "./PeriodicityDialog.vue";
import api, {
  LANG_TABS,
  LANG_LABELS,
  displayLangText,
  contrastTextColor,
  apiErrorMessage,
} from "./psdsCalendarApi";

export default {
  name: "PsdsCalendarPeriodicityList",
  components: { MfEmpty, PeriodicityDialog },
  data() {
    return {
      Edit,
      Delete,
      loading: false,
      rows: [],
      dialogVisible: false,
      editing: null,
      togglingId: null,
      deletingId: null,
      langTabs: LANG_TABS,
      langLabels: LANG_LABELS,
    };
  },
  computed: {
    // Yangi davriylikka tartib raqami avtomatik: mavjudlaridagi eng kattasi + 1.
    nextOrder() {
      const orders = this.rows
          .map((row) => Number(row.order_number))
          .filter((value) => Number.isFinite(value));
      return orders.length ? Math.max(...orders) + 1 : 1;
    },
  },
  created() {
    this.fetchList();
  },
  methods: {
    title(row) {
      return displayLangText(row.title) || "—";
    },
    rowClass({ row }) {
      return row.is_active === false ? "fc-row--inactive" : "";
    },
    badgeStyle(row) {
      return { background: row.color, color: contrastTextColor(row.color) };
    },
    fetchList() {
      this.loading = true;
      api
          .periodicities()
          .then((rows) => {
            this.rows = rows;
          })
          .catch((error) => {
            ElNotification.error({ title: "Xatolik", message: apiErrorMessage(error) });
          })
          .finally(() => {
            this.loading = false;
          });
    },
    openCreate() {
      this.editing = null;
      this.dialogVisible = true;
    },
    openEdit(row) {
      this.editing = row;
      this.dialogVisible = true;
    },
    // PUT to'liq almashtirish: faqat `is_active` ni yuborsak qolgan hamma
    // maydon default'ga tushib ketadi. Shuning uchun avval to'liq yozuvni
    // olib, ustidan qaytaramiz (JAVOBLAR.md A3).
    onToggleActive(row) {
      this.togglingId = row.id;
      api
          .periodicity(row.id)
          .then((data) =>
              api.updatePeriodicity(row.id, {
                code: data.code,
                title: data.title,
                color: data.color,
                order_number: data.order_number,
                is_active: !(data.is_active !== false),
              })
          )
          .then(() => {
            ElMessage({ type: "success", message: "Holati yangilandi" });
          })
          .catch((error) => {
            ElNotification.error({ title: "Xatolik", message: apiErrorMessage(error) });
          })
          .finally(() => {
            this.togglingId = null;
            this.fetchList();
          });
    },
    onDelete(row) {
      ElMessageBox.confirm(
          `«${this.title(row)}» davriyligini o'chirasizmi? Admin paneldan qaytarib bo'lmaydi.`,
          "Davriylikni o'chirish",
          { confirmButtonText: "Ha, o'chirish", cancelButtonText: "Bekor qilish", type: "warning" }
      )
          .then(() => {
            this.deletingId = row.id;
            api
                .deletePeriodicity(row.id)
                .then(() => {
                  ElMessage({ type: "success", message: "Davriylik o'chirildi" });
                  this.fetchList();
                })
                .catch((error) => {
                  ElNotification.error({
                    title: "O'chirib bo'lmadi",
                    message: apiErrorMessage(error),
                    duration: 8000,
                  });
                  // Bog'langan eventlar bo'lsa backend 400 qaytaradi — adminga
                  // ular qaysi yillarda ekanini ko'rsatamiz, aks holda qidirish
                  // uchun yildan yilga yurishga to'g'ri keladi.
                  if (error?.response?.status === 400) this.showBlockingEvents(row);
                })
                .finally(() => {
                  this.deletingId = null;
                });
          })
          .catch(() => {});
    },
    showBlockingEvents(row) {
      api
          .events({ periodicity_id: row.id })
          .then((events) => {
            if (!events.length) return;
            const byYear = events.reduce((acc, event) => {
              acc[event.year] = (acc[event.year] || 0) + 1;
              return acc;
            }, {});
            const summary = Object.keys(byYear)
                .sort((a, b) => b - a)
                .map((year) => `${year}: ${byYear[year]} ta`)
                .join(", ");
            ElNotification.warning({
              title: "Bog'langan hodisalar",
              message: `${summary}. Kalendar sahifasida shu yillarni ochib o'chiring yoki boshqa davriylikka ko'chiring.`,
              duration: 0,
            });
          })
          .catch(() => {});
    },
  },
};
</script>

<style scoped lang="scss">
@use "./psds-calendar.scss";

.fc-code {
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f4f8;
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 12px;
  color: #4a5468;
}

.fc-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
}
</style>
